package com.dotdrives.web.pages.donations

import com.dotdrives.controller.DonationDetailController
import com.dotdrives.controller.DonationsLedgerController
import com.dotdrives.controller.NeedsAttentionController
import com.dotdrives.controller.QuickRecordController
import com.dotdrives.controller.YearEndReceiptsController
import com.dotdrives.repository.SupabaseDonationRepository
import com.dotdrives.repository.SupabaseGiverRepository
import com.dotdrives.web.shell.NavItem
import com.dotdrives.web.shell.PortalShell
import com.dotdrives.web.ui.useFlowState
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.cancel
import react.ChildrenBuilder
import react.Key
import react.FC
import react.Props
import react.useEffect
import react.useState

private class DonationsControllerHolder(
    val scope: kotlinx.coroutines.CoroutineScope,
    val ledger: DonationsLedgerController,
    val attention: NeedsAttentionController,
    val detail: DonationDetailController,
    val quickRecord: QuickRecordController,
    val yearEnd: YearEndReceiptsController,
)

external interface DonationsPortalProps : Props {
    var currentUserRole: String
    var orgId: String
    var userId: String
    var userInitials: String
    var userName: String
    var orgName: String
    var canWrite: Boolean
    var onSwitchOrg: () -> Unit
    var onNavigate: (NavItem) -> Unit
    var onSignOut: () -> Unit
}

val DonationsPortal = FC<DonationsPortalProps> { props ->
    val (holder) = useState {
        val scope = MainScope()
        val donationRepo = SupabaseDonationRepository()
        val giverRepo = SupabaseGiverRepository()
        DonationsControllerHolder(
            scope = scope,
            ledger = DonationsLedgerController(donationRepo, scope, props.orgId),
            attention = NeedsAttentionController(donationRepo, scope, props.orgId),
            detail = DonationDetailController(donationRepo, scope, props.orgId),
            quickRecord = QuickRecordController(giverRepo, donationRepo, scope, props.orgId),
            yearEnd = YearEndReceiptsController(donationRepo, scope, props.orgId),
        )
    }
    val giftWorkflow = com.dotdrives.web.ui.useWorkflowController(props.userId, props.orgId, props.currentUserRole, "manual-gift")
    val giftWorkflowState = useFlowState(giftWorkflow.state)
    val ledgerState = useFlowState(holder.ledger.state)
    val attentionState = useFlowState(holder.attention.state)
    val detailState = useFlowState(holder.detail.state)
    val quickRecordState = useFlowState(holder.quickRecord.state)
    val yearEndState = useFlowState(holder.yearEnd.state)

    useEffect(holder) {
        try {
            awaitCancellation()
        } finally {
            holder.scope.cancel()
        }
    }

    PortalShell {
        active = NavItem.Donations
        onNavigate = props.onNavigate
        userInitials = props.userInitials
        userName = props.userName
        orgName = props.orgName
        onSwitchOrg = props.onSwitchOrg
        onSignOut = props.onSignOut

        DonationsPage {
            giftController = giftWorkflow
            giftState = giftWorkflowState
            userId = props.userId; orgId = props.orgId; currentUserRole = props.currentUserRole
            ledgerController = holder.ledger
            this.ledgerState = ledgerState
            attentionController = holder.attention
            this.attentionState = attentionState
            detailController = holder.detail
            this.detailState = detailState
            quickRecordController = holder.quickRecord
            this.quickRecordState = quickRecordState
            yearEndController = holder.yearEnd
            this.yearEndState = yearEndState
            canWrite = props.canWrite
            onNavigate = props.onNavigate
        }
    }
}

/** Keep mount-owned controllers and persisted actions within the active account and organization. */
internal fun ChildrenBuilder.accountDonationsPortal(
    accountId: String,
    organizationId: String,
    component: FC<DonationsPortalProps> = DonationsPortal,
    initialize: DonationsPortalProps.() -> Unit,
) {
    component {
        initialize()
        key = "$accountId:$organizationId".unsafeCast<Key>()
        userId = accountId
        orgId = organizationId
    }
}
